import { useRef, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Send, Paperclip, X, AlertTriangle, Eye } from "lucide-react";
import { SavedReplies } from "./SavedReplies";
import { useAuth } from "../../context/AuthContext";

const MAX = 10 * 1024 * 1024;
const BLANK = "[FIRST NAME]";
const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
export const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const firstName = (name) => {
  const w = (name || "").trim().split(/\s+/)[0] || "";
  return !w || w.includes("@") ? "" : w[0].toUpperCase() + w.slice(1);
};

const AttachList = ({ files, fwd, fwdOn, setFwdOn, remove }) => (
  <div className="space-y-2">
    {fwd?.length > 0 && (
      <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={fwdOn} onCheckedChange={setFwdOn} data-testid="mail-compose-fwd-atts" />
        Include original attachments ({fwd.map((a) => a.filename).join(", ")})</label>
    )}
    {files.length > 0 && <div className="flex gap-2 flex-wrap" data-testid="mail-compose-files">{files.map((f, i) => (
      <span key={`${f.name}-${f.size}-${f.lastModified}`} className="inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-lg pl-2.5 pr-1 py-1 bg-slate-50">
        <Paperclip size={12} />{f.name} <span className="text-slate-400">{fmtSize(f.size)}</span>
        <button onClick={() => remove(i)} className="p-0.5 rounded hover:bg-slate-200" data-testid={`mail-compose-file-remove-${i}`} aria-label="Remove"><X size={12} /></button>
      </span>
    ))}</div>}
  </div>
);

export const MailCompose = ({ draft, mailboxes, onClose, onSent }) => {
  const { user } = useAuth();
  const own = [...new Set([...(draft.assigned_from ? [draft.assigned_from] : []), ...mailboxes.map((b) => b.address)])];
  const mine = mailboxes.find((b) => b.user_id === user?.user_id)?.address;
  const [from, setFrom] = useState(own.includes(draft.from) ? draft.from : mine || own[0] || "");
  const [to, setTo] = useState(draft.to || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const [files, setFiles] = useState([]);
  const [fwdOn, setFwdOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState(draft.lang === "fr" ? "fr" : "en");
  const [langTouched, setLangTouched] = useState(false);
  const pickLang = (l) => { setLang(l); setLangTouched(true); };
  const autoLang = () => {
    const one = split(to);
    if (langTouched || draft.lang || one.length !== 1) return;
    api.get("/staff/mail-lang", { params: { email: one[0] } }).then((r) => setLang(r.data.lang)).catch(() => {});
  };
  const [preview, setPreview] = useState("");
  const showPreview = () => api.post("/staff/mail-preview", { from_address: from, body, lang })
    .then((r) => setPreview(r.data.html)).catch((e) => toast.error(errText(e)));
  const fileRef = useRef(null);
  const fwdSize = fwdOn ? (draft.forward_atts || []).reduce((s, a) => s + (a.size || 0), 0) : 0;
  const total = files.reduce((s, f) => s + f.size, 0) + fwdSize;
  const hasBlank = body.includes(BLANK);

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list || [])];
    if (next.reduce((s, f) => s + f.size, 0) + fwdSize > MAX) toast.error("Attachments can be up to 10 MB in total");
    else setFiles(next);
    if (fileRef.current) fileRef.current.value = "";
  };
  const insert = (t) => {
    const txt = t.replaceAll("{first_name}", firstName(draft.to_name) || BLANK);
    setBody((b) => (/^\n\n(On |Le |-{10} Forwarded)/.test(b) ? `${txt}${b}` : b.trim() ? `${b.trimEnd()}\n\n${txt}` : txt));
  };
  const send = () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("payload", JSON.stringify({ from_address: from, to: split(to), cc: split(cc), subject, body,
      reply_to_id: draft.reply_to_id || "", forward_id: draft.forward_id || "", forward_attachments: fwdOn, lang }));
    files.forEach((f) => fd.append("files", f));
    api.post("/staff/mail/send", fd)
      .then(() => { toast.success("Email sent"); onSent(); })
      .catch((e) => toast.error(errText(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mail-compose-dialog">
        <DialogHeader><DialogTitle>{draft.reply_to_id ? "Reply" : draft.forward_id ? "Forward" : "New email"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[60px_1fr] items-center gap-2 text-sm">
            <span className="text-slate-500">From</span>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="mail-compose-from"><SelectValue /></SelectTrigger>
              <SelectContent>{own.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-slate-500">To</span>
            <Input value={to} onChange={(e) => setTo(e.target.value)} onBlur={autoLang} placeholder="name@example.com" data-testid="mail-compose-to" />
            <span className="text-slate-500">Cc</span>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="optional" data-testid="mail-compose-cc" />
            <span className="text-slate-500">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mail-compose-subject" />
            <span className="text-slate-500">Design</span>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5" data-testid="mail-compose-lang">
                {[["en", "English"], ["fr", "Français"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => pickLang(v)} data-testid={`mail-compose-lang-${v}`}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${lang === v ? "bg-[#12234A] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
                ))}
              </div>
              <span className="text-xs text-slate-400">Language of the CritterPosts footer{!langTouched && (draft.lang || lang === "fr") ? " · picked for this customer" : ""}</span>
            </div>
          </div>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} placeholder="Write your message…" data-testid="mail-compose-body"
            className={hasBlank ? "border-amber-400 ring-1 ring-amber-300" : ""} />
          {hasBlank && <p className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="mail-compose-blank-warning">
            <AlertTriangle size={14} /> Replace {BLANK} with the customer's name before sending.</p>}
          <AttachList files={files} fwd={draft.forward_atts} fwdOn={fwdOn} setFwdOn={setFwdOn} remove={(i) => setFiles(files.filter((_, x) => x !== i))} />
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="mail-compose-file-input" />
          <div className="flex justify-between gap-2 flex-wrap">
            <div className="flex gap-2 items-center">
              <SavedReplies onInsert={insert} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="mail-compose-attach"><Paperclip size={15} /> Attach</Button>
              <Button variant="outline" size="sm" onClick={showPreview} disabled={!from} className="gap-1.5" data-testid="mail-compose-preview"><Eye size={15} /> Preview</Button>
              {total > 0 && <span className="text-xs text-slate-400" data-testid="mail-compose-total">{fmtSize(total)} / 10 MB</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} data-testid="mail-compose-cancel">Cancel</Button>
              <Button onClick={send} disabled={busy || hasBlank || !from || !to.trim() || !subject.trim() || !body.trim()} data-testid="mail-compose-send" className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5"><Send size={15} /> {busy ? "Sending…" : "Send"}</Button>
            </div>
          </div>
        </div>
        {preview && (
          <Dialog open onOpenChange={(o) => !o && setPreview("")}>
            <DialogContent className="max-w-3xl" data-testid="mail-preview-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
              <DialogHeader><DialogTitle>Email preview</DialogTitle></DialogHeader>
              <iframe title="Email preview" sandbox="allow-same-origin" tabIndex={-1} srcDoc={preview} className="w-full h-[70vh] rounded-xl border border-slate-200" data-testid="mail-preview-frame" />
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};
